// Recovery for stale code-split chunks after a redeploy.
//
// Routes are React.lazy(() => import(...)) → content-hashed Vite chunks. Firebase
// Hosting serves only the current deploy's files, so a tab running an older build
// references chunk hashes that no longer exist. Navigating to such a route throws
// "Failed to fetch dynamically imported module" and (before this) blanked the app.
//
// The running build is simply stale, so the fix is to reload once and pull the
// fresh index.html + new chunk hashes. Guarded by a short time window so a chunk
// that genuinely can't be fetched (offline, real 404) doesn't reload-loop — after
// the guard trips, callers fall back to showing the error UI.

const RELOAD_TS_KEY = "ir-chunk-reload-ts";
const RELOAD_WINDOW_MS = 15_000;

export const isChunkLoadError = (err: unknown): boolean => {
    const msg = (err instanceof Error ? err.message : String(err ?? "")).toLowerCase();
    return msg.includes("failed to fetch dynamically imported module")
        || msg.includes("error loading dynamically imported module")
        || msg.includes("importing a module script failed")
        || msg.includes("failed to import");
};

// Returns true if it triggered a reload, false if suppressed (reloaded very
// recently) so the caller can show a recovery UI instead of looping.
export const reloadOnceForStaleChunk = (): boolean => {
    try {
        const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || "0");
        if (Date.now() - last < RELOAD_WINDOW_MS)
            return false;
        sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
    } catch {
        // sessionStorage unavailable — reload anyway, just without the guard.
    }
    window.location.reload();
    return true;
};
